import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
  orUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveDate,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateADUAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];
  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    !subjectImprovements ||
    !compImprovements ||
    !depreciationRatios ||
    !subjectPropertyIdentification ||
    !compPropertyIdentification ||
    !effectiveDate
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { adu } = subjectImprovements;
  const { adu: compADU } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    const subjectAreaSqft = ORDER_FEATURES.adu.hasFeature(subjectImprovements)
      ? orUndefined(AloftArea.schema.parse)(adu?.area)?.toSqFt().unitAmount
      : undefined;
    const compAreaSqft = ORDER_FEATURES.adu.hasFeature(compImprovements)
      ? orUndefined(AloftArea.schema.parse)(compADU?.area)?.toSqFt().unitAmount
      : undefined;
    return calculateUnitRateAdjustment({
      subjectUnitAmount: subjectAreaSqft,
      compUnitAmount: compAreaSqft,
      unitRate,
    });
  }

  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) return undefined;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  const aduArea =
    adu && adu.area && isDefined(adu.quality) && ORDER_FEATURES.adu.hasFeature(subjectImprovements)
      ? AloftArea.schema.parse(adu.area)
      : AloftArea.zero;
  const compAduArea =
    compADU &&
    compADU.area &&
    isDefined(compADU.quality) &&
    ORDER_FEATURES.adu.hasFeature(compImprovements)
      ? AloftArea.schema.parse(compADU.area)
      : AloftArea.zero;

  return adjCalculator.getADUAdj({
    subject:
      adu && aduArea && adu.quality
        ? {
            sqft: aduArea,
            quality: adu.quality,
            fullBaths: adu.fullBaths,
            halfBaths: adu.halfBaths,
            zipCode,
          }
        : undefined,
    comp:
      compADU && compAduArea && compADU.quality
        ? {
            sqft: compAduArea,
            quality: compADU.quality,
            fullBaths: compADU.fullBaths,
            halfBaths: compADU.halfBaths,
            zipCode: compZipCode,
          }
        : undefined,
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

export const selectADUAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'adu'),
    selectEffectiveDate,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'adu'),
  ],
  calculateADUAdj,
);

export const selectADUAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'adu'),
    selectEffectiveDate,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'adu'),
  ],
  (
    adjustmentVersion,
    improvements,
    propertyIdentification,
    depreciationRatios,
    effectiveDate,
    compSequence,
    compTypes,
    salesComparisonApproach,
    unitRate,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateADUAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        effectiveDate,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedADUAdj = createPriceSelector(
  [selectADUAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'adu', adjSettings),
);

export const selectAppliedADUAdjDisplay = createSelector([selectAppliedADUAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedADUAdjsDisplay = createSelector(
  [selectADUAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = adjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.adu;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[id] = {
          value: getStringValueFromAdjustment(applyRoundingAndThresholds(adj, 'adu', adjSettings)),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
